import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import api from '../services/api';
import { toast } from 'sonner';
import {
  Gavel, TrendingDown, AlertTriangle, MessageSquare, ChevronLeft,
  Check, History, Zap, Trophy, ArrowDown, Shield, Bell, Activity
} from 'lucide-react';

const POLL_MS = 2500;

export default function LiveAuctionScreen({ event, token, onBack }) {
  const PORTAL_API = '/api/supplier-portal';
  const authH = { headers: { Authorization: `Bearer ${token}` } };

  const [liveData, setLiveData] = useState(null);
  const [myBids, setMyBids] = useState(event.my_bids || []);
  const [selected, setSelected] = useState(null);
  const [bidValue, setBidValue] = useState('');
  const [bidError, setBidError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [messages, setMessages] = useState([]);
  const [extension, setExtension] = useState(null);
  const [justSubmitted, setJustSubmitted] = useState(null);
  const [outbidFlash, setOutbidFlash] = useState({});
  const pollRef = useRef(null);
  const prevBestRef = useRef({});
  const prevClosingRef = useRef(event.closing_date);
  const bidInputRef = useRef(null);

  const structure = event.bidding_structure || 'multi_line';
  const items = event.auction_items || event.line_items || [];
  const lots = event.auction_lots || event.lots || [];
  const settings = event.auction_settings || {};
  const visibility = event.bid_visibility || 'rank_only';
  const minDecrement = settings.minimum_decrement || 0;
  const rows = structure === 'lot_based' ? lots : items;
  const isSingleBid = structure === 'single_item' || structure === 'total_basket';

  // ── Poll for live data ──
  const poll = useCallback(async () => {
    try {
      const r = await api.get(`${PORTAL_API}/event/${event.event_ref}`, authH);
      const d = r.data;
      setMyBids(d.my_bids || []);
      setMessages(d.broadcast_messages || []);
      setLiveData(d);
      // Extension detection
      if (d.closing_date && d.closing_date !== prevClosingRef.current) {
        prevClosingRef.current = d.closing_date;
        setExtension(`Auction extended! New close: ${new Date(d.closing_date).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Los_Angeles' })}`);
        setTimeout(() => setExtension(null), 10000);
      }
    } catch {}
  }, [event.event_ref, token]); // eslint-disable-line

  useEffect(() => {
    poll();
    // Only poll when event is live or paused — stop for ended events
    const eventStatus = liveData?.status || event.status;
    if (['OPEN', 'PAUSED'].includes(eventStatus)) {
      pollRef.current = setInterval(poll, POLL_MS);
    }
    return () => clearInterval(pollRef.current);
  }, [poll, liveData?.status]); // eslint-disable-line

  // Auto-select first row
  useEffect(() => {
    if (!selected && rows.length > 0 && !isSingleBid) {
      setSelected(rows[0].item_id || rows[0].lot_id || rows[0].id);
    }
  }, [rows, selected, isSingleBid]);

  // ── Countdown ──
  const [countdown, setCountdown] = useState({ h: 0, m: 0, s: 0, total: 0 });
  useEffect(() => {
    const tick = () => {
      const closing = liveData?.closing_date || event.closing_date;
      if (!closing) return;
      const diff = Math.max(0, (new Date(closing) - new Date()) / 1000);
      setCountdown({ h: Math.floor(diff / 3600), m: Math.floor((diff % 3600) / 60), s: Math.floor(diff % 60), total: diff });
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [liveData?.closing_date, event.closing_date]);

  const isLive = (liveData?.status || event.status) === 'OPEN';
  const isPaused = (liveData?.status || event.status) === 'PAUSED';
  const isAwarded = (liveData?.status || event.status) === 'AWARDED';
  const isEnded = ['AWARDED', 'CLOSED', 'CANCELLED', 'EVALUATION', 'AWARD_PENDING'].includes(liveData?.status || event.status);
  const isClosed = !isLive && !isPaused;
  const urgentTimer = countdown.total < 300 && isLive; // < 5 min, only when live

  // ── My best bids ──
  const myBestByRow = {};
  let myBestTotal = null;
  for (const b of myBids) {
    if (b.total_amount != null && (myBestTotal === null || b.total_amount < myBestTotal)) myBestTotal = b.total_amount;
    for (const lb of (b.line_bids || [])) {
      const k = lb.item_id;
      if (!myBestByRow[k] || lb.bid_price < myBestByRow[k]) myBestByRow[k] = lb.bid_price;
    }
    for (const lb of (b.lot_bids || [])) {
      const k = lb.lot_id;
      if (!myBestByRow[k] || lb.bid_amount < myBestByRow[k]) myBestByRow[k] = lb.bid_amount;
    }
  }

  // ── Selected row info ──
  const selectedRow = isSingleBid ? { description: structure === 'single_item' ? 'Single Item Bid' : 'Total Basket Bid' }
    : rows.find(r => (r.item_id || r.lot_id || r.id) === selected);
  const selectedBest = isSingleBid ? myBestTotal : (selected ? myBestByRow[selected] : null);

  // ── Bid validation ──
  const validateBid = (val) => {
    const v = parseFloat(val);
    if (!val || isNaN(v)) return 'Enter a bid amount';
    if (v <= 0) return 'Bid must be positive';
    if (selectedBest != null && minDecrement > 0 && v > selectedBest - minDecrement) {
      return `Bid must be at least $${minDecrement} lower than your best ($${selectedBest.toLocaleString()})`;
    }
    return '';
  };

  // ── Submit bid ──
  const handleBid = async () => {
    const err = validateBid(bidValue);
    if (err) { setBidError(err); return; }
    setBidError('');
    setSubmitting(true);
    try {
      let payload = {};
      if (isSingleBid) {
        payload = { bid_amount: parseFloat(bidValue) };
      } else if (structure === 'multi_line') {
        const item = items.find(i => (i.item_id || i.id) === selected);
        payload = { line_bids: [{ item_id: selected, bid_price: parseFloat(bidValue), quantity: item?.quantity || 1 }] };
      } else if (structure === 'lot_based') {
        payload = { lot_bids: [{ lot_id: selected, bid_amount: parseFloat(bidValue) }] };
      }
      const r = await api.post(`${PORTAL_API}/event/${event.event_ref}/bid`, payload, authH);
      setJustSubmitted({ amount: r.data.total_amount, at: new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Los_Angeles' }) });
      setTimeout(() => setJustSubmitted(null), 4000);
      setBidValue('');
      toast.success(`Bid accepted — $${r.data.total_amount?.toLocaleString()}`);
      poll();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Bid rejected';
      setBidError(detail);
      toast.error(detail);
    }
    setSubmitting(false);
    bidInputRef.current?.focus();
  };

  // ── Render ──
  const pad = (n) => String(n).padStart(2, '0');

  return (
    <div className="flex flex-col h-full bg-slate-50" data-testid="live-auction-v2">
      {/* Back */}
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-indigo-600 hover:underline px-4 py-2">
        <ChevronLeft className="w-4 h-4" /> Exit Auction
      </button>

      {/* ═══════ HEADER — Timer + Status ═══════ */}
      <div className={`mx-4 rounded-2xl p-6 mb-4 transition-colors ${isAwarded ? 'bg-indigo-900' : isPaused ? 'bg-amber-900' : isEnded ? 'bg-slate-800' : urgentTimer ? 'bg-red-900' : 'bg-slate-900'} text-white`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Gavel className="w-6 h-6" />
            <div>
              <h1 className="text-lg font-bold">{event.title}</h1>
              <span className="text-xs opacity-60">{event.event_ref} · {structure.replace(/_/g, ' ')}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isPaused && <span className="flex items-center gap-1.5 bg-amber-500 text-white px-3 py-1 rounded-full text-sm font-bold animate-pulse"><AlertTriangle className="w-4 h-4" /> PAUSED</span>}
            {isLive && <span className="flex items-center gap-1.5 bg-emerald-500 text-white px-3 py-1 rounded-full text-sm font-bold"><span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"/><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white"/></span> LIVE</span>}
            {isAwarded && <span className="bg-indigo-400 text-white px-3 py-1 rounded-full text-sm font-bold">AWARDED</span>}
            {isClosed && !isAwarded && !isPaused && <span className="bg-slate-500 text-white px-3 py-1 rounded-full text-sm font-bold">{liveData?.status || event.status}</span>}
          </div>
        </div>

        {/* Show timer only when live/paused, show outcome when ended */}
        {isEnded ? (
          <div className="text-center py-6">
            {isAwarded ? (
              <>
                <Trophy className="w-12 h-12 mx-auto mb-3 text-amber-400" />
                {event.awarded_to && event.awarded_to.includes && myBids.length > 0 ? (
                  <>
                    <h2 className="text-2xl font-bold text-white mb-2">Auction Awarded</h2>
                    <p className="text-sm opacity-80">
                      This auction has been awarded. {event.awarded_at ? `Decision made on ${new Date(event.awarded_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}.` : ''}
                    </p>
                    <p className="text-xs opacity-60 mt-2">You will receive an email notification with the outcome details.</p>
                  </>
                ) : (
                  <>
                    <h2 className="text-2xl font-bold text-white mb-2">Auction Awarded</h2>
                    <p className="text-sm opacity-80">
                      This auction has concluded and been awarded. {event.awarded_at ? `Decision made on ${new Date(event.awarded_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}.` : ''}
                    </p>
                    <p className="text-xs opacity-60 mt-2">Check your email for the outcome notification.</p>
                  </>
                )}
              </>
            ) : (
              <>
                <Shield className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <h2 className="text-xl font-bold text-white mb-2">Auction {liveData?.status || event.status}</h2>
                <p className="text-sm opacity-70">This auction is no longer accepting bids. The buyer is currently evaluating submissions.</p>
              </>
            )}
          </div>
        ) : (
          <>
            {/* Big countdown */}
            <div className="text-center">
              <div className="flex items-center justify-center gap-3">
                <div className="text-center"><p className={`text-6xl font-mono font-black tracking-wider ${urgentTimer ? 'text-red-300' : ''}`}>{pad(countdown.h)}</p><p className="text-[10px] uppercase tracking-wider opacity-40 mt-1">Hours</p></div>
                <span className="text-4xl font-mono opacity-30 mt-[-12px]">:</span>
                <div className="text-center"><p className={`text-6xl font-mono font-black tracking-wider ${urgentTimer ? 'text-red-300' : ''}`}>{pad(countdown.m)}</p><p className="text-[10px] uppercase tracking-wider opacity-40 mt-1">Minutes</p></div>
                <span className="text-4xl font-mono opacity-30 mt-[-12px]">:</span>
                <div className="text-center"><p className={`text-6xl font-mono font-black tracking-wider ${urgentTimer ? 'animate-pulse text-red-300' : ''}`}>{pad(countdown.s)}</p><p className="text-[10px] uppercase tracking-wider opacity-40 mt-1">Seconds</p></div>
              </div>
            </div>
            {/* Extension alert */}
            {extension && (
              <div className="mt-3 bg-blue-600 rounded-lg px-4 py-2 text-center text-sm font-bold animate-bounce">
                <Bell className="w-4 h-4 inline mr-2" />{extension}
              </div>
            )}
          </>
        )}
      </div>

      {/* ═══════ MAIN GRID ═══════ */}
      <div className="flex gap-4 flex-1 min-h-0 mx-4 mb-4">
        {/* ── Left: Items/Lots Table ── */}
        <div className="flex-1 flex flex-col">
          <div className="border rounded-2xl bg-white overflow-hidden flex-1 flex flex-col">
            <div className="overflow-y-auto flex-1">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 z-10">
                  <tr className="border-b">
                    <th className="text-left px-4 py-3 font-semibold w-10">#</th>
                    <th className="text-left px-4 py-3 font-semibold">{structure === 'lot_based' ? 'Lot' : 'Item'}</th>
                    {structure === 'multi_line' && <th className="text-right px-4 py-3 font-semibold w-20">Qty</th>}
                    <th className="text-right px-4 py-3 font-semibold w-36">Your Best Bid</th>
                    {visibility !== 'own_only' && <th className="text-center px-4 py-3 font-semibold w-28">{visibility === 'rank_only' ? 'Rank' : visibility === 'best_anonymous' ? 'Market Best' : 'Position'}</th>}
                    <th className="text-center px-4 py-3 font-semibold w-24">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(isSingleBid ? [{ id: '__total', description: structure === 'single_item' ? 'Total Bid' : 'Basket Total', quantity: null }] : rows).map((row, idx) => {
                    const rowId = row.item_id || row.lot_id || row.id || `row_${idx}`;
                    const isSelected = (isSingleBid ? true : selected === rowId);
                    const myBest = isSingleBid ? myBestTotal : myBestByRow[rowId];
                    const hasBid = myBest != null;
                    return (
                      <tr key={rowId}
                        onClick={() => { if (!isSingleBid) { setSelected(rowId); setBidValue(''); setBidError(''); bidInputRef.current?.focus(); } }}
                        className={`border-b transition-colors cursor-pointer ${
                          isSelected ? 'bg-indigo-50 border-l-4 border-l-indigo-500' :
                          outbidFlash[rowId] ? 'bg-red-50' : 'hover:bg-slate-50'
                        }`}>
                        <td className="px-4 py-3 text-xs text-slate-400 font-mono">{isSingleBid ? '' : idx + 1}</td>
                        <td className="px-4 py-3 font-medium">{row.description || row.name}</td>
                        {structure === 'multi_line' && <td className="px-4 py-3 text-right text-slate-500">{row.quantity} {row.uom}</td>}
                        <td className="px-4 py-3 text-right">
                          {hasBid ? (
                            <span className="font-mono font-bold text-indigo-700 text-base">${myBest.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                          ) : <span className="text-slate-300 text-xs">No bid</span>}
                        </td>
                        {visibility !== 'own_only' && (
                          <td className="px-4 py-3 text-center">
                            {visibility === 'rank_only' && hasBid && <Badge className="bg-slate-100 text-slate-600 text-xs">Ranked</Badge>}
                            {!hasBid && <span className="text-slate-300 text-xs">—</span>}
                          </td>
                        )}
                        <td className="px-4 py-3 text-center">
                          {hasBid ? (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full">
                              <Check className="w-3 h-3" /> Bid placed
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">Awaiting bid</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Table footer summary */}
            <div className="bg-slate-50 border-t px-4 py-2 flex items-center justify-between text-xs">
              <span className="text-slate-500">{rows.length} {structure === 'lot_based' ? 'lot(s)' : 'item(s)'} · {myBids.length} bid(s) placed</span>
              {myBestTotal != null && <span className="font-bold text-indigo-700">Best Total: ${myBestTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>}
            </div>
          </div>
        </div>

        {/* ── Right: Bid Panel (only when live/paused) ── */}
        <div className="w-80 flex-shrink-0 flex flex-col gap-3">
          {isEnded ? (
            /* Award outcome panel */
            <div className="border rounded-2xl bg-white p-5 shadow-sm text-center">
              <Trophy className="w-8 h-8 mx-auto mb-2 text-amber-500" />
              <h3 className="font-bold text-sm mb-2">{isAwarded ? 'Auction Awarded' : 'Auction Ended'}</h3>
              <p className="text-xs text-slate-500">{isAwarded ? 'The buyer has made their award decision. Check your email for the outcome.' : 'This auction is no longer accepting bids.'}</p>
              {myBids.length > 0 && (
                <div className="mt-3 bg-slate-50 rounded-lg p-3 text-left">
                  <p className="text-xs text-slate-400 mb-1">Your final bid</p>
                  <p className="text-lg font-bold text-indigo-700">${myBestTotal != null ? myBestTotal.toLocaleString('en-US', { minimumFractionDigits: 2 }) : '—'}</p>
                  <p className="text-xs text-slate-400 mt-1">{myBids.length} bid(s) placed</p>
                </div>
              )}
            </div>
          ) : (
          /* Bid Entry Card */
          <div className="border rounded-2xl bg-white p-5 shadow-sm">
            <h3 className="font-bold text-sm mb-1 flex items-center gap-2"><Zap className="w-4 h-4 text-amber-500" /> Place Your Bid</h3>
            {selectedRow ? (
              <div className="space-y-3 mt-3">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider">Bidding on</p>
                  <p className="text-sm font-semibold truncate">{selectedRow.description || selectedRow.name}</p>
                  {selectedBest != null && (
                    <p className="text-xs text-slate-500 mt-1">Your current best: <strong className="text-indigo-700">${selectedBest.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong></p>
                  )}
                </div>
                <div>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-2xl font-bold text-slate-300">$</span>
                    <input ref={bidInputRef} type="number" value={bidValue}
                      onChange={e => { setBidValue(e.target.value); setBidError(validateBid(e.target.value)); }}
                      onKeyDown={e => { if (e.key === 'Enter') handleBid(); }}
                      placeholder="0.00" disabled={!isLive || isPaused}
                      className="w-full h-14 pl-10 pr-4 text-2xl font-mono font-bold border-2 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-colors disabled:opacity-50 disabled:bg-slate-50"
                      data-testid="bid-input" />
                  </div>
                  {minDecrement > 0 && <p className="text-[10px] text-slate-400 mt-1">Min. decrement: ${minDecrement} ({settings.decrement_type === 'percentage' ? '%' : 'fixed'})</p>}
                </div>
                {/* Validation error */}
                {bidError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-xs flex items-start gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />{bidError}
                  </div>
                )}
                {/* Success flash */}
                {justSubmitted && (
                  <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-2 rounded-lg text-xs flex items-center gap-2 animate-pulse">
                    <Check className="w-3.5 h-3.5" /> Bid accepted — ${justSubmitted.amount?.toLocaleString()} at {justSubmitted.at}
                  </div>
                )}
                <Button onClick={handleBid} disabled={submitting || !isLive || isPaused || !bidValue}
                  className={`w-full h-12 text-base font-bold rounded-xl transition-colors ${
                    !isLive || isPaused ? 'bg-slate-300' :
                    submitting ? 'bg-indigo-400' : 'bg-emerald-600 hover:bg-emerald-700 active:scale-[0.98]'
                  }`} data-testid="submit-bid-btn">
                  {submitting ? <span className="flex items-center gap-2"><span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> Placing...</span>
                    : isPaused ? <span className="flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Auction Paused</span>
                    : !isLive ? 'Auction Not Live'
                    : <span className="flex items-center gap-2"><TrendingDown className="w-5 h-5" /> Place Bid</span>}
                </Button>
              </div>
            ) : (
              <p className="text-sm text-slate-400 mt-3">Select an item from the table to place a bid.</p>
            )}
          </div>
          )}

          {/* Bid History */}
          <div className="border rounded-2xl bg-white p-4 flex-1 overflow-hidden flex flex-col">
            <h3 className="font-bold text-xs mb-2 flex items-center gap-2 text-slate-500 uppercase tracking-wider"><History className="w-3.5 h-3.5" /> Your Bid History</h3>
            {myBids.length === 0 ? <p className="text-xs text-slate-400 flex-1 flex items-center justify-center">No bids yet</p> : (
              <div className="space-y-1 overflow-y-auto flex-1">
                {myBids.slice(0, 15).map((b, i) => (
                  <div key={i} className="flex items-center justify-between text-xs py-1.5 px-2 rounded hover:bg-slate-50">
                    <span className="font-mono font-bold text-indigo-700">${b.total_amount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    <span className="text-slate-400">{b.placed_at ? new Date(b.placed_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'America/Los_Angeles' }) : ''}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ═══════ FOOTER — Messages + Info ═══════ */}
      <div className="mx-4 mb-4 flex gap-4">
        {/* Buyer Messages */}
        <div className="flex-1 border rounded-2xl bg-white p-4 max-h-32 overflow-y-auto">
          <h3 className="font-bold text-xs mb-2 flex items-center gap-2 text-slate-500 uppercase tracking-wider"><MessageSquare className="w-3.5 h-3.5" /> Buyer Messages</h3>
          {messages.length === 0 ? <p className="text-xs text-slate-400">No messages from the buyer.</p> : (
            <div className="space-y-1.5">
              {messages.map((m, i) => (
                <div key={i} className="text-xs bg-blue-50 rounded-lg px-3 py-2">
                  <span className="text-blue-400 font-mono">{m.sent_at ? new Date(m.sent_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Los_Angeles' }) : ''}</span>
                  <p className="text-blue-800 mt-0.5">{m.message}</p>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Auction Info */}
        <div className="w-64 flex-shrink-0 border rounded-2xl bg-white p-4">
          <h3 className="font-bold text-xs mb-2 text-slate-500 uppercase tracking-wider flex items-center gap-2"><Shield className="w-3.5 h-3.5" /> Auction Rules</h3>
          <div className="space-y-1 text-xs text-slate-600">
            <p>Structure: <strong>{structure.replace(/_/g, ' ')}</strong></p>
            <p>Visibility: <strong>{visibility.replace(/_/g, ' ')}</strong></p>
            {minDecrement > 0 && <p>Min decrement: <strong>${minDecrement}</strong></p>}
            {settings.auto_extend && <p>Anti-sniping: <strong>+{settings.extension_minutes || 5} min</strong> if bid in last {settings.extension_minutes || 5} min</p>}
            {settings.max_extensions && <p>Max extensions: <strong>{settings.max_extensions}</strong></p>}
          </div>
        </div>
      </div>
    </div>
  );
}
